// @flow
import { gql } from 'graphql-request';

import { runQuery } from '_client';
import { GqlError } from '_utils/errors';

const CREATE_SETUP_INTENT = gql`
  mutation createSetupIntent($requestId: UID!, $setupIntent: SetupIntentArgs) {
    createSetupIntent(requestId: $requestId, setupIntent: $setupIntent) {
      id
      clientSecret
      description
      status
      usage
      nextAction
    }
  }
`;

export const createSetupIntent = async (requestId: string, paymentMethodId: string): Promise<any> => {
  try {
    const { createSetupIntent: res } = await runQuery(CREATE_SETUP_INTENT, {
      requestId,
      setupIntent: {
        paymentMethodId,
      },
    });
    return res;
  } catch (err) {
    throw new GqlError(err);
  }
};

const CREATE_SUBSCRIPTION = gql`
  mutation createSubscription($requestId: UID!, $subscription: createSubscriptionArgs!) {
    createSubscription(requestId: $requestId, subscription: $subscription) {
      id
      status
      cancelAtPeriodEnd
      currentPeriodEnd
      currentPeriodStart
      pendingSetupIntent
      cancelAt
      canceledAt
      created
      discount
      endedAt
      trialEnd
      trialStart
      latestInvoice {
        id
        amountDue
        total
        amountPaid
        currency
        paymentIntent {
          id
          clientSecret
          description
          status
          usage
        }
      }
    }
  }
`;

export const createSubscription = async (requestId: string, subscription: Object): Promise<Object> => {
  try {
    const { createSubscription: res } = await runQuery(CREATE_SUBSCRIPTION, {
      requestId,
      subscription,
    });
    return res;
  } catch (err) {
    if (!err.response.data?.createSubscription?.status) {
      throw new GqlError(err);
    }

    return err.response.data.createSubscription;
  }
};

const CANCEL_SUBSCRIPTION = gql`
  mutation cancelSubscription($requestId: UID!, $subscriptionId: ID!) {
    cancelSubscription(requestId: $requestId, subscriptionId: $subscriptionId) {
      id
      canceledAt
    }
  }
`;

export const cancelSubscription = async (requestId: string, subscriptionId: string): Promise<Object> => {
  try {
    const { cancelSubscription: res } = await runQuery(CANCEL_SUBSCRIPTION, { requestId, subscriptionId });
    return res;
  } catch (err) {
    throw new GqlError(err);
  }
};

const DETACH_PAYMENT_METHOD = gql`
  mutation detachPaymentMethod($requestId: UID!, $paymentMethodId: ID!) {
    detachPaymentMethod(requestId: $requestId, paymentMethodId: $paymentMethodId)
  }
`;

export const detachPaymentMethod = async (requestId: string, paymentMethodId: string): Promise<void> => {
  try {
    const done = await runQuery(DETACH_PAYMENT_METHOD, { requestId, paymentMethodId });
    if (!done) {
      throw new Error('failed to detach payment method');
    }
    return;
  } catch (err) {
    throw new GqlError(err);
  }
};

const SET_DEFAULT_PAYMENT_METHOD = gql`
  mutation setDefaultPaymentMethod($requestId: UID!, $paymentMethodId: ID!) {
    setDefaultPaymentMethod(requestId: $requestId, paymentMethodId: $paymentMethodId)
  }
`;

export const setDefaultPaymentMethod = async (requestId: string, paymentMethodId: string): Promise<boolean> => {
  try {
    const { setDefaultPaymentMethod: res } = await runQuery(SET_DEFAULT_PAYMENT_METHOD, {
      requestId,
      paymentMethodId,
    });
    return res;
  } catch (err) {
    throw new GqlError(err);
  }
};

const REDEEM_PROMOTION_CODE = gql`
  mutation redeemPromotionCode($promotionCodeId: String!) {
    redeemPromotionCode(promotionCodeId: $promotionCodeId) {
      id
      status
      cancelAtPeriodEnd
      currentPeriodEnd
      currentPeriodStart
      pendingSetupIntent
      cancelAt
      canceledAt
      created
      discount
      endedAt
      trialEnd
      trialStart
      latestInvoice {
        id
        amountDue
        total
        amountPaid
        currency
      }
    }
  }
`;

export const doRedeemPromotionCode = async (promotionCodeId: string): Promise<Object> => {
  try {
    const { redeemPromotionCode: res } = await runQuery(REDEEM_PROMOTION_CODE, {
      promotionCodeId,
    });
    return res;
  } catch (err) {
    throw new GqlError(err);
  }
};

const CREATE_PAYMENT_INTENT = gql`
  mutation createPaymentIntent($requestId: ID!, $priceId: ID!, $paymentMethodId: ID!) {
    createPaymentIntent(requestId: $requestId, priceId: $priceId, paymentMethodId: $paymentMethodId) {
      id
      clientSecret
      description
      status
      usage
      paymentMethod {
        id
        billingDetails
        type
        card {
          expMonth
          expYear
          last4
          brand
        }
      }
      nextAction
    }
  }
`;

export const createPaymentIntent = async (requestId: string, priceId: string, paymentMethodId: string): Promise<Object> => {
  try {
    const { createPaymentIntent: res } = await runQuery(CREATE_PAYMENT_INTENT, {
      requestId,
      priceId,
      paymentMethodId,
    });
    return res;
  } catch (err) {
    throw new GqlError(err);
  }
};

const PROMOTION_CODE = gql`
  query promotionCode($code: String!) {
    promotionCode(code: $code) {
      id
      active
      code
      coupon {
        id
        name
        amountOff
        percentOff
        valid
      }
      metadata
      constraints
    }
  }
`;

export const getPromotionCode = async (code: string): Promise<Object> => {
  try {
    const { promotionCode } = await runQuery(PROMOTION_CODE, {
      code,
    });
    return promotionCode;
  } catch (err) {
    throw new GqlError(err);
  }
};